import {
    Accordion,
    Box,
    Flex,
    HStack,
    Image,
    Stack,
    Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AccItem } from "../jewellery/components/FilterMenu";
import OurServices from "../home/components/OurServices";
import RingBanner from "../../assets/jewellery/ring-banner.png";
import Loading from "../Errorandloading/Loading";
import axiosInstance from "../../config/axiosInstance";
import MediaWithLoader from "../productDetails/MediaWithLoader";
import Review1 from "../productDetails/components/Review1";
import WeCare from "../productDetails/components/WeCare";
import BookAnAppointment from "../productDetails/components/BookAnAppointment";
import BespokePurchaseCard from "./BespokePurchaseCard";
import SEO from '../Blogs/Seo'

const BespokeProductDetails = () => {
    const { product_id } = useParams();
    const [product, setProduct] = useState(null);
    const [variants, setVariants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Variant selection state
    const [selectedShape, setSelectedShape] = useState("");
    const [selectedSize, setSelectedSize] = useState("");
    const [selectedColor, setSelectedColor] = useState(""); // Diamond color (E/F)
    const [selectedClarity, setSelectedClarity] = useState("");
    const [selectedGoldClarity, setSelectedGoldClarity] = useState(""); // Gold purity (e.g., 14KT)
    const [selectedGoldColor, setSelectedGoldColor] = useState(""); // Metal color (White/Yellow/...)

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setLoading(true);
                const res = await axiosInstance.get(`/api/bespoke/${product_id}`);
                const fetchedProduct = res.data.product;
                const fetchedVariants = res.data.variants;
                setProduct(fetchedProduct);
                setVariants(fetchedVariants);

                // Initialize selections from pricing baseConfig where possible
                const baseConfig = fetchedProduct?.pricing?.baseConfig || {};
                const defaultShape = baseConfig.stoneShape || "round";
                const defaultDiamondColor = baseConfig.color || "F";
                const defaultClarity = baseConfig.clarity || "VS1";
                setSelectedShape(defaultShape);
                setSelectedColor(defaultDiamondColor);
                setSelectedClarity(defaultClarity);

                // Gold purity defaults to 14KT if available, else first option
                const availableGoldPurities = fetchedProduct?.goldclarity || [];
                const purity14 = availableGoldPurities.find((p) => String(p).includes("14"));
                setSelectedGoldClarity(purity14 || availableGoldPurities[0] || "");

                // Gold color defaults to first product option or first variant color
                if (fetchedProduct?.goldcolor?.length > 0) {
                    setSelectedGoldColor(fetchedProduct.goldcolor[0]);
                } else if (fetchedVariants?.length > 0) {
                    setSelectedGoldColor((fetchedVariants[0].color || "").charAt(0).toUpperCase() + (fetchedVariants[0].color || "").slice(1));
                }

                // Size default from pricing if available (show first size)
                const sizeAdjustments = fetchedProduct?.pricing?.caratStepAdjustments || [];
                if (sizeAdjustments.length > 0) {
                    setSelectedSize(String(sizeAdjustments[0].step));
                }
            } catch (err) {
                setError(err?.response?.data?.error || "Failed to load product");
            } finally {
                setLoading(false);
            }
        };

        if (product_id) fetchProduct();
    }, [product_id]);

    if (loading) return <Loading />;
    if (error) return <Box p={4} color="red.500">Error: {error}</Box>;
    if (!product) return null;

    // Find selected variant based on current selections
    // Map variant by stone shape and metal color to drive media display
    const selectedVariant = variants.find((v) => {
        const variantColor = (v.color || "").toLowerCase();
        const selectedMetalColor = (selectedGoldColor || "").toLowerCase();
        return v.stoneShape === selectedShape && variantColor === selectedMetalColor;
    });

    // Media of selected variant only
    const mediaItems = [
        ...(selectedVariant?.videos || []),
        ...(selectedVariant?.images || []),
    ];

    // Get the first image for social sharing if available
    const firstImage = mediaItems.find(item => !item.endsWith('.mp4')) || 
                      "https://www.rrumidiamond.com/images/default-social.jpg";

    return (
        <>
            {/* SEO Component with product-specific metadata */}
            <SEO
                pageTitle={`${product.name} | Bespoke Jewelry | R'Rumi Diamond`}
                metaDescription={`Custom ${product.name} - ${product.description.substring(0, 160)}...`}
                metaKeywords={[
                    `Bespoke ${product.name}`,
                    "Custom Jewelry",
                    "Handmade Diamond Jewelry",
                    ...product.tags || []
                ]}
                ogTitle={`${product.name} | Custom Design | R'Rumi Diamond`}
                ogDescription={`Create your perfect ${product.name}. ${product.description.substring(0, 140)}...`}
                ogImage={firstImage}
                ogType="product"
            >
                {/* Structured data for product */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": product.name,
                        "description": product.description,
                        "image": firstImage,
                        "brand": {
                            "@type": "Brand",
                            "name": "R'Rumi Diamond"
                        },
                        "offers": {
                            "@type": "Offer",
                            "priceCurrency": "USD",
                            "price": selectedVariant?.price || product.startingPrice,
                            "availability": "https://schema.org/InStock",
                            "itemCondition": "https://schema.org/NewCondition"
                        }
                    })}
                </script>
            </SEO>

            <Box pt={20}>
                {/* Mobile View */}
                <Box display={{ base: "block", md: "none" }} px={4}>
                    <Flex gap={4} overflowX="auto" pb={4} pt={4} scrollSnapType="x mandatory">
                        {mediaItems.map((media, i) => (
                            <Box key={i} minW="100%" maxW="100%" flexShrink={0} scrollSnapAlign="center">
                                <MediaWithLoader
                                    media={media}
                                    type={media.endsWith(".mp4") ? "video" : "image"}
                                    aspectRatio="1 / 1"
                                />
                            </Box>
                        ))}
                    </Flex>

                    <Box mt={4}>
                        <BespokePurchaseCard
                            product={product}
                            variants={variants}
                            selectedShape={selectedShape}
                            setSelectedShape={setSelectedShape}
                            selectedSize={selectedSize}
                            setSelectedSize={setSelectedSize}
                            selectedColor={selectedColor}
                            setSelectedColor={setSelectedColor}
                            selectedClarity={selectedClarity}
                            setSelectedClarity={setSelectedClarity}
                            selectedGoldClarity={selectedGoldClarity}
                            setSelectedGoldClarity={setSelectedGoldClarity}
                            selectedGoldColor={selectedGoldColor}
                            setSelectedGoldColor={setSelectedGoldColor}
                            selectedVariant={selectedVariant}
                        />
                    </Box>
                </Box>

                {/* Desktop View */}
                <HStack display={{ base: "none", md: "flex" }} align="flex-start" spacing={0}>
                    <Box flex={{ base: 1, md: 3 }} mt={-4}>
                        {mediaItems.map((media, i) => (
                            <MediaWithLoader
                                key={i}
                                media={media}
                                type={media.endsWith(".mp4") ? "video" : "image"}
                            />
                        ))}
                    </Box>
                    <Box
                        position="sticky"
                        top="0"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flex={{ base: 1, md: 1 }}
                        p={4}
                    >
                        <BespokePurchaseCard
                            product={product}
                            variants={variants}
                            selectedShape={selectedShape}
                            setSelectedShape={setSelectedShape}
                            selectedSize={selectedSize}
                            setSelectedSize={setSelectedSize}
                            selectedColor={selectedColor}
                            setSelectedColor={setSelectedColor}
                            selectedClarity={selectedClarity}
                            setSelectedClarity={setSelectedClarity}
                            selectedGoldClarity={selectedGoldClarity}
                            setSelectedGoldClarity={setSelectedGoldClarity}
                            selectedGoldColor={selectedGoldColor}
                            setSelectedGoldColor={setSelectedGoldColor}
                            selectedVariant={selectedVariant}
                        />
                    </Box>
                </HStack>

                {/* Product Info */}
                <Stack maxW="95%" mx="auto" my={6} gap={6}>
                    {product?.reviews?.length > 0 && <Review1 productId={product_id} />}
                    <Accordion allowToggle>
                        <AccItem title="Product Details" data={product} />
                        <AccItem title="Our Diamonds" data={product} />
                        <AccItem title="Free Shipping & Returns" />
                        <AccItem title="Craftsmanship & Warranty" />
                    </Accordion>
                    <BookAnAppointment />
                </Stack>

                <WeCare />

                {/* Banner */}
                <Box pos="relative">
                    <Image src={RingBanner} w="full" maxH="md" objectFit="cover" />
                    <Box pos="absolute" bottom={4} maxW="container.md" left={4} color="white">
                        <Text fontSize={{ base: "sm", sm: "3xl" }}>
                            Jewell Best Collection For You All
                        </Text>
                        <Text fontSize={{ base: "10px", sm: "md" }}>
                            Discover timeless elegance with Jewell's finest collection, curated
                            to celebrate every moment. Crafted with precision and passion, these
                            pieces are designed to shine with you, always.
                        </Text>
                    </Box>
                </Box>

                <OurServices />
            </Box>
        </>
    );
};

export default BespokeProductDetails;